import { ComponentPropsWithoutRef, FC, ReactNode, use } from 'react';
import type NextImage from 'next/image';
import { isContentfulAssetProxyUrl, isContentfulImageUrl } from '../../../utils/contentful';
type NextImageSrc = ComponentPropsWithoutRef<typeof NextImage>['src'];
type PlaceholderImageProps = Pick<ComponentPropsWithoutRef<typeof NextImage>, 'placeholder' | 'blurDataURL'>;
type Props = {
  src: NextImageSrc;
  children: (props: PlaceholderImageProps) => ReactNode;
};
type WindowWithPlaceholders = Window & {
  _tuic_placeholders: Record<string, string>;
};
export const PlaceholderDataProvider: FC<Props> = ({
  src,
  children
}) => {
  if (typeof src !== 'string') {
    return children({});
  }
  if (typeof window !== 'undefined' && isWindowWithPlaceholders(window) && typeof window._tuic_placeholders[src] !== 'undefined') {
    return <>
        {children({
        blurDataURL: window._tuic_placeholders[src],
        placeholder: 'blur'
      })}
        {createPlaceholderInlineScript(src, window._tuic_placeholders[src])}
      </>;
  }
  if (typeof window !== 'undefined') {
    return children({});
  }
  let blurDataUrl = use(prefetchPlaceholder(src));
  if (typeof blurDataUrl === 'undefined') {
    return children({});
  }
  return <>
      {children({
      blurDataURL: blurDataUrl,
      placeholder: 'blur'
    })}
      {createPlaceholderInlineScript(src, blurDataUrl)}
    </>;
};
const isWindowWithPlaceholders = (win: Window | WindowWithPlaceholders | undefined): win is WindowWithPlaceholders => {
  return typeof win !== 'undefined' && '_tuic_placeholders' in win;
};
const createPlaceholderInlineScript = (src: string, blurDataUrl: string) => <script suppressHydrationWarning dangerouslySetInnerHTML={{
  __html: `
      (function() {
        window._tuic_placeholders = window._tuic_placeholders || {};
        window._tuic_placeholders['${src}'] = '${blurDataUrl}';
      })();
    `
}} data-sentry-component="createPlaceholderInlineScript" data-sentry-source-file="PlaceholderDataProvider.tsx" />;
async function prefetchPlaceholder(src: string): Promise<string | undefined> {
  if (!isContentfulImageUrl(src) && !isContentfulAssetProxyUrl(src)) {
    return undefined;
  }
  const miniVersionUrl = new URL(src);
  miniVersionUrl.searchParams.set('w', '8');
  miniVersionUrl.searchParams.set('h', '8');
  const {
    downloadAndEncodeImageBase64
  } = await import('./base64-image');
  return await downloadAndEncodeImageBase64(miniVersionUrl.href);
}